/**
 * WorkflowHitlHistorySection — chronological audit trail of HITL Q&A events
 * for a workflow instance. Mirrors WorkflowArtifactsSection's "no fetch,
 * parents own data" pattern so ora and the outlook addin can share a single
 * shared component while owning their own transports.
 *
 * Each event is rendered as a collapsible card:
 *   - header (always visible): "HITL #N — {phase_id} — {answered_at} · {summary} · {latency} · decided by {name}"
 *   - body (expanded): the prompt_text as shown at decision time (``**bold**``
 *     resolved via renderInlineBold, same as the live panels), the response rendered via
 *     <JsonSchemaForm readOnly>, and a small thumbnail strip of any
 *     image-kind URIs in artifacts_snapshot.
 *
 * PENDING ASKS (2026-09, HITL history parity): the substrate now writes a row
 * the moment a question is ASKED, not only once it's answered. Such a row
 * carries ``event_type: "asked"``, ``asked_at`` set, and ``answered_at`` /
 * ``answered_by`` / ``answered_by_name`` / ``response`` all null. The SAME
 * row is updated in place to ``event_type: "decision"`` once the AM
 * responds — this is a point-in-time view of one row, not a distinct row
 * kind. While pending, the header reads "HITL #N — {step} — asked
 * {asked_at} · awaiting decision" with no latency and no "decided by" (both
 * are naturally absent since answered_at/answered_by are null); the body
 * still renders the prompt exactly as an answered row would, and the
 * response area shows "Awaiting decision" instead of the usual "—"
 * placeholder. If ``answered_at`` is ever set while ``event_type`` is still
 * "asked" (defensive — should not occur), the row renders as an ordinary
 * decision row.
 *
 * Each card carries `id="hitl-event-${sequence}"` so pipeline-badge clicks
 * can scrollIntoView() the matching card.
 */

import { useMemo, useState } from "react";
import { formatCapturedAt } from "~/common/workflows/components/WorkflowArtifactsSection";
import type { WorkflowArtifact } from "~/common/workflows/components/WorkflowArtifactsSection";
import WorkflowArtifactsGallery, {
  type FetchArtifactPayloadFn,
} from "~/common/workflows/components/WorkflowArtifactsGallery";
import { HitlResponseRenderer } from "~/common/workflows/components/HitlResponseRenderer";
import { formatDecisionLatency } from "~/common/workflows/decisionLatency";
import { summarizeHitlResponse } from "~/common/workflows/hitlSummary";
import { renderInlineBold } from "~/common/workflows/renderInlineBold";

export interface HitlEvent {
  sequence: number;
  step_id: string;
  phase_id: string | null;
  event_type: string;
  question: Record<string, unknown> | null;
  prompt_text: string | null;
  response: Record<string, unknown> | null;
  artifacts_snapshot: string[];
  asked_at: string;
  answered_at: string | null;
  answered_by: string | null;
  answered_by_name: string | null;
}

export interface WorkflowHitlHistorySectionProps {
  events: HitlEvent[];
  loading?: boolean;
  heading?: string;
  /** Required for api_evidence on-demand payload in the evidence strip. */
  instanceId?: string | null;
  fetchArtifactPayload?: FetchArtifactPayloadFn;
  /**
   * Sequence of the event the parent wants opened by default. Used to
   * support deep-linking from a pipeline-badge click. Optional.
   */
  initialOpenSequence?: number;
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp)$/i;
const TRACE_EXTENSION = /trace[^/]*\.zip$/i;
const DOM_EXTENSIONS = /\.html?$/i;

/** Convert a snake_case step_id ("review_screenshots") to a display label
 *  ("Review Screenshots"). Used in the card header so the user sees the
 *  prettified step name instead of the raw substrate identifier. */
function prettyStepLabel(stepId: string): string {
  return stepId
    .split("_")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

/** Derive a WorkflowArtifact (kind/url/filename/step_name) from a bare URI.
 *
 * The substrate stores ``artifacts_snapshot`` as ``string[]`` — just URIs —
 * so we infer kind from the filename extension and use the filename stem
 * as the step_name fallback. The Product API has already SAS-signed each
 * URI before this component sees it.
 *
 * Exported (2026-09, 26742) so the run-record page's Decisions section can
 * turn a HITL-derived decision line's `artifacts_snapshot` into the same
 * `WorkflowArtifact[]` shape `ArtifactLightbox` expects, without restating
 * this kind-sniffing logic. */
export function uriToArtifact(uri: string): WorkflowArtifact {
  let pathname = uri;
  try {
    pathname = new URL(uri).pathname;
  } catch {
    // Bare path / file:// fallback — keep the raw string.
  }
  const filename = pathname.split("/").pop() ?? uri;
  const stem = filename.replace(/\.[^.]+$/, "");
  let kind = "unknown";
  if (IMAGE_EXTENSIONS.test(filename)) kind = "screenshot";
  else if (TRACE_EXTENSION.test(filename)) kind = "trace";
  else if (DOM_EXTENSIONS.test(filename)) kind = "dom";
  return {
    kind,
    url: uri,
    filename,
    step_name: stem,
  };
}

function getQuestionMessage(ev: HitlEvent): string | null {
  if (ev.prompt_text && ev.prompt_text.trim().length > 0) return ev.prompt_text;
  const q = ev.question;
  if (!q) return null;
  for (const key of ["message", "description", "prompt_text", "title"]) {
    const v = q[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return null;
}

function getQuestionSchema(ev: HitlEvent): Record<string, unknown> | null {
  const q = ev.question;
  if (!q) return null;
  const s = q.schema;
  if (s && typeof s === "object" && !Array.isArray(s)) {
    return s as Record<string, unknown>;
  }
  return null;
}

/** Recognized keys inside ``question.data`` that workflows use to declare
 *  which artifacts the question explicitly presented to the AM. The first
 *  matching key with a non-empty array wins. */
const QUESTION_ARTIFACT_KEYS = ["screenshots", "attachments", "artifacts"];

/** Identity for matching a question URI against the signed snapshot.
 *
 * The substrate's SAS issuer rewrites the host of every signed blob URI
 * to whatever ``WorkflowsArtifacts.AccountUrl`` is configured against
 * (e.g. ``azurite:10000`` → ``127.0.0.1:10000`` in dev compose), so a
 * full-URL strip-query comparison silently fails. The pathname is
 * stable across both the raw question URI and the signed snapshot URI
 * for the same blob, so we key the lookup table on it instead.
 *
 * Falls back to the input string for non-URL inputs (e.g. ``file://``
 * or bare paths) so they still produce a stable key. */
function blobIdentity(uri: string): string {
  try {
    return new URL(uri).pathname;
  } catch {
    const q = uri.indexOf("?");
    return q >= 0 ? uri.slice(0, q) : uri;
  }
}

/** Pull the URIs the question payload claimed to present.
 *
 * Two reasons this is preferred over ``event.artifacts_snapshot``:
 *
 *   1. **Semantic accuracy.** ``artifacts_snapshot`` records every artifact
 *      visible at decision time — a useful audit signal but not the same as
 *      "what this question showed". A multi-HITL workflow whose second
 *      step is a confirmation has the same files on disk as the first; the
 *      snapshot would mis-suggest the confirmation step also presented
 *      them.
 *   2. **Workflow opt-in.** A workflow signals "I describe my own context"
 *      simply by setting ``question.data`` to an object. The renderer
 *      reads any of the recognised artifact keys (``screenshots`` /
 *      ``attachments`` / ``artifacts``) and trusts the workflow's
 *      declaration — including the empty case ("this question presents
 *      nothing"). Legacy workflows that omit ``data`` entirely fall
 *      through to the broad ``artifacts_snapshot`` so they keep working.
 *
 * Return values:
 *   - ``string[]`` (possibly empty) when the question opted into the
 *     convention. Empty array means "explicitly presented nothing" —
 *     suppresses the strip even when files exist on disk.
 *   - ``null`` when the question has no ``data`` object at all → caller
 *     falls back to ``artifacts_snapshot``. */
function getQuestionArtifactUris(ev: HitlEvent): string[] | null {
  const q = ev.question;
  if (!q || typeof q !== "object") return null;
  const data = (q as Record<string, unknown>).data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const dataObj = data as Record<string, unknown>;
  // The presence of ``data`` itself is the opt-in signal. Walk the
  // recognised keys to gather URIs; an empty result means "explicitly none".
  const uris: string[] = [];
  for (const key of QUESTION_ARTIFACT_KEYS) {
    const v = dataObj[key];
    if (!Array.isArray(v)) continue;
    for (const item of v) {
      if (typeof item === "string") {
        uris.push(item);
      } else if (item && typeof item === "object") {
        const o = item as Record<string, unknown>;
        const candidate = o.uri ?? o.url ?? o.href;
        if (typeof candidate === "string") uris.push(candidate);
      }
    }
  }
  return uris;
}

function LoadingSkeleton({ heading }: { heading?: string }) {
  return (
    <div
      className="flex flex-col gap-2 mt-2"
      data-testid="hitl-history-loading"
      aria-label={`${heading || "Human-In-The-Loop Decisions"} (loading)`}
    >
      {[0, 1].map((i) => (
        <div
          key={i}
          className="h-14 rounded-md bg-primary-gray-light-100 animate-pulse"
        />
      ))}
    </div>
  );
}

function EmptyState({ heading }: { heading?: string }) {
  return (
    <div
      className="rounded-md border border-dashed border-primary-gray-light-300 bg-primary-gray-light-25 px-3 py-4 text-center mt-2"
      data-testid="hitl-history-empty"
      aria-label={heading || "Human-In-The-Loop Decisions"}
    >
      <p className="text-xs text-primary-gray-light-500">
        No Human-In-The-Loop decisions recorded for this run.
      </p>
    </div>
  );
}

/**
 * Tightly-scoped attribution line — shows who approved and when.
 *
 * Shared between ora's drawer and the outlook plugin's drawer (both consume
 * ``WorkflowHitlHistorySection`` from this file). Rendered as a single
 * ``text-xs`` line in a darker gray than the surrounding latency / summary
 * text so the approver attribution stands out.
 *
 * Returns ``null`` when neither piece of context is available — callers do
 * not need to wrap it in a conditional.
 */
export function HitlReviewerLine({
  reviewerName,
  answeredAt,
  className = "",
}: {
  reviewerName?: string | null;
  answeredAt?: string | null;
  className?: string;
}) {
  const formattedAt = formatCapturedAt(answeredAt ?? null);
  const parts = [reviewerName?.trim() || null, formattedAt].filter(
    Boolean,
  ) as string[];
  if (parts.length === 0) return null;
  return (
    <span
      className={`block truncate text-xs font-medium text-primary-gray-light-700 ${className}`}
      data-testid="hitl-reviewer-line"
    >
      {parts.join(" · ")}
    </span>
  );
}

/**
 * The expanded body of one HITL event: the prompt as it was shown at decision
 * time, the response, and the artifacts the question presented.
 *
 * Split out of `HitlEventCard` so the run record's Decisions ledger can drill
 * into a HITL-sourced line WITHOUT re-implementing any of it. The card owns the
 * header and the disclosure; this owns what is disclosed. Two callers, one
 * renderer — which is the whole point: everything below (the `renderInlineBold`
 * fix, the SAS-swap matching, `HitlResponseRenderer`) is subtle enough that a
 * second copy would drift immediately.
 *
 * `bodyId` is supplied by the caller because it is the target of that caller's
 * own `aria-controls`.
 */
export function HitlEventDetail({
  event,
  bodyId,
  instanceId,
  fetchArtifactPayload,
}: {
  event: HitlEvent;
  bodyId: string;
  instanceId?: string | null;
  fetchArtifactPayload?: FetchArtifactPayloadFn;
}) {
  const promptText = getQuestionMessage(event);
  const schema = getQuestionSchema(event);

  // Prefer the artifacts the question explicitly presented (read from
  // ``question.data.screenshots`` / ``attachments`` / ``artifacts``); fall
  // back to ``artifacts_snapshot`` only when the workflow doesn't declare
  // its own list. Keeps a confirmation-only follow-up HITL from
  // mis-claiming the screenshots its predecessor showed.
  //
  // ``artifacts_snapshot`` URIs are SAS-signed by the API handler; the
  // URIs inside ``question`` are persisted verbatim (raw blob paths) so
  // the browser can't load them. When the question lists a URI that also
  // appears in the snapshot, we swap in the signed version. Items the
  // snapshot doesn't cover pass through unchanged (best-effort).
  //
  // Matching is by URL pathname rather than full URL because the SAS
  // issuer rewrites the host (e.g. ``azurite:10000`` → ``127.0.0.1:10000``).
  const snapshotArtifacts = useMemo<WorkflowArtifact[]>(() => {
    const presented = getQuestionArtifactUris(event);
    const rawUris = presented ?? event.artifacts_snapshot;
    const signedByPath = new Map<string, string>();
    for (const signed of event.artifacts_snapshot) {
      signedByPath.set(blobIdentity(signed), signed);
    }
    const resolvedUris = rawUris.map(
      (u) => signedByPath.get(blobIdentity(u)) ?? u,
    );
    return resolvedUris.map(uriToArtifact);
  }, [event]);

  return (
    <div
      id={bodyId}
      className="px-3 pb-3 pt-1 border-t border-primary-gray-light-200 flex flex-col gap-3"
    >
      {/* renderInlineBold matches every LIVE HITL panel (HitlChoicePanel,
          HitlQuestionPanel, HitlFileUploadDecisionPanel) — without it the
          recorded copy of the SAME prompt rendered its ``**bold**`` as
          literal asterisks, so a gate that read correctly while open
          degraded the moment it became an audit row (found via instance
          a7abccb1, carrier_action_instruction). whitespace-pre-wrap
          already preserved the \n\n breaks; only the bold was missing. */}
      {promptText && (
        <div className="text-sm text-primary-gray-light-800 whitespace-pre-wrap">
          {renderInlineBold(promptText)}
        </div>
      )}

      <section aria-labelledby={`${bodyId}-response`} className="flex flex-col gap-2">
        <h4
          id={`${bodyId}-response`}
          className="text-xs font-bold uppercase tracking-wide text-primary-gray-light-700"
        >
          Response
        </h4>
        <HitlResponseRenderer
          response={event.response}
          schema={schema}
          eventType={event.event_type}
        />
      </section>

      {snapshotArtifacts.length > 0 && (
        <WorkflowArtifactsGallery
          artifacts={snapshotArtifacts}
          variant="strip"
          instanceId={instanceId}
          fetchArtifactPayload={fetchArtifactPayload}
        />
      )}
    </div>
  );
}

function HitlEventCard({
  event,
  expanded,
  onToggle,
  instanceId,
  fetchArtifactPayload,
}: {
  event: HitlEvent;
  expanded: boolean;
  onToggle: () => void;
  instanceId?: string | null;
  fetchArtifactPayload?: FetchArtifactPayloadFn;
}) {
  // A pending ask: recorded when the question went out, not yet answered.
  // See the file-header comment for the full row-grammar contract. Gating
  // on this alone is enough — answered_at/answered_by/answered_by_name are
  // null on a genuinely pending row, so latency and the reviewer line fall
  // out to null/empty on their own without a second check.
  const isPendingAsk = event.event_type === "asked" && !event.answered_at;

  const summary = useMemo(
    () =>
      isPendingAsk
        ? "Awaiting decision"
        : summarizeHitlResponse(event.response, event.event_type),
    [event.response, event.event_type, isPendingAsk],
  );
  const latency = formatDecisionLatency(event.asked_at, event.answered_at);
  const askedAtDisplay = formatCapturedAt(event.asked_at);
  const answeredAtDisplay = formatCapturedAt(event.answered_at ?? event.asked_at);
  // Pending rows show "asked {asked_at}" instead of the answered-at
  // timestamp — there is no decision time yet.
  const timeDisplay = isPendingAsk ? `asked ${askedAtDisplay}` : answeredAtDisplay;
  const reviewerLabel =
    event.answered_by_name?.trim() ||
    (event.answered_by ? "Resumer" : null);

  // Use the step label (e.g. "Review Screenshots") rather than the technical
  // phase_id ("mock_rpa_cnbc_browse.browse_and_review") — prettier and more
  // useful per-event since one phase often hosts multiple HITL steps.
  const headerParts = [
    `Human-In-The-Loop #${event.sequence}`,
    prettyStepLabel(event.step_id),
    timeDisplay,
    summary,
    latency,
    reviewerLabel ? `decided by ${reviewerLabel}` : null,
  ].filter(Boolean);

  const bodyId = `hitl-event-body-${event.sequence}`;

  return (
    <div
      id={`hitl-event-${event.sequence}`}
      role="listitem"
      className="rounded-md border border-primary-gray-light-200 bg-base-white"
      data-testid={`hitl-event-${event.sequence}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className="flex w-full items-center gap-2 px-3 py-2 text-left rounded-md hover:bg-primary-gray-light-25 focus-visible:outline focus-visible:outline-1 focus-visible:outline-sunrise"
      >
        <span
          aria-hidden="true"
          className={`text-primary-gray-light-500 transition-transform ${expanded ? "rotate-90" : ""}`}
        >
          ▸
        </span>
        <span className="flex-1 min-w-0">
          <span className="block truncate text-sm font-semibold text-primary-gray-light-900">
            {headerParts.slice(0, 4).join(" — ")}
          </span>
          <HitlReviewerLine
            reviewerName={event.answered_by_name}
            answeredAt={event.answered_at}
          />
          <span className="block truncate text-xs text-primary-gray-light-500">
            {headerParts.slice(4).join(" · ")}
          </span>
        </span>
      </button>

      {expanded && (
        <HitlEventDetail
          event={event}
          bodyId={bodyId}
          instanceId={instanceId}
          fetchArtifactPayload={fetchArtifactPayload}
        />
      )}
    </div>
  );
}

export function WorkflowHitlHistorySection({
  events,
  loading = false,
  heading,
  instanceId,
  fetchArtifactPayload,
  initialOpenSequence,
}: WorkflowHitlHistorySectionProps) {
  const [openSet, setOpenSet] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    if (initialOpenSequence != null) initial.add(initialOpenSequence);
    return initial;
  });

  if (loading) return <LoadingSkeleton heading={heading} />;
  if (events.length === 0) return <EmptyState heading={heading} />;

  function toggle(sequence: number) {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(sequence)) next.delete(sequence);
      else next.add(sequence);
      return next;
    });
  }

  return (
    <div
      className="flex flex-col gap-2 mt-2"
      role="list"
      data-testid="hitl-history-list"
      aria-label={heading || "Human-In-The-Loop Decisions"}
    >
      {heading && (
        <p className="text-xs font-semibold text-primary-gray-light-600 mb-1">
          {heading}
        </p>
      )}
      {events.map((ev) => (
        <HitlEventCard
          key={ev.sequence}
          event={ev}
          expanded={openSet.has(ev.sequence)}
          onToggle={() => toggle(ev.sequence)}
          instanceId={instanceId}
          fetchArtifactPayload={fetchArtifactPayload}
        />
      ))}
    </div>
  );
}

export default WorkflowHitlHistorySection;
